/**
 * Team settings (phase 6 §5) — /teams/:id, restyled with the phase-7 kit
 * inside the responsive DashShell. All behavior is unchanged: members see
 * the roster, settings, and enroll instructions; owners also add/remove
 * members and edit the freeform settings key/values; invitees accept.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../dashboard.css";
import { useAuth } from "../hooks/useAuth";
import { DashShell, SecLabel } from "../components/DashShell";
import {
  acceptTeamInvite,
  addTeamMember,
  ApiError,
  getTeam,
  putTeamSettings,
  removeTeamMember,
  type TeamDetail,
} from "../lib/api";
import { getTenantId, setTenantId } from "../lib/tenant";
import { timeAgo } from "../lib/format";
import { Card, Btn, MonoChip, StatusPill, Icon, I } from "../kit";

const POLL_MS = 30_000;

// Suggested settings keys (phase 6 §1): freeform map the plane never
// interprets — team agents read these through their prompts/CLAUDE.md.
const SUGGESTED_KEYS = ["githubOrg", "linearWorkspace", "conventions"] as const;

interface SettingRow {
  key: string;
  value: string;
}

export default function TeamSettings() {
  const { id } = useParams<{ id: string }>();
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();

  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // members
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<"member" | "owner">("member");
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberErr, setMemberErr] = useState<string | null>(null);

  // invite acceptance (contract §1: invitee accepts → status "member")
  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState<string | null>(null);

  // settings editor — local rows, saved as one map (PUT replaces it).
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const t = await getTeam(id);
      setTeam(t);
      setErr(null);
      // Seed the editor once; don't clobber in-progress edits on poll.
      setRows((r) =>
        r === null
          ? Object.entries(t.settings ?? {}).map(([key, value]) => ({
              key,
              value,
            }))
          : r
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to load");
    }
  }, [id]);

  useEffect(() => {
    if (status === "signed-out") navigate("/", { replace: true });
  }, [status, navigate]);

  useEffect(() => {
    if (status !== "signed-in") return;
    refresh();
    const iv = setInterval(refresh, POLL_MS);
    return () => clearInterval(iv);
  }, [status, refresh]);

  const myEmail = user?.email;
  const me = team?.members.find((m) => m.email === myEmail);
  // Prefer the plane's own answer (myStatus on GET /teams/{id}); fall back
  // to my roster row for planes that omit it.
  const isInvited =
    team?.myStatus === "invited" ||
    (team?.myStatus === undefined && me?.status === "invited");
  const isOwner =
    !isInvited &&
    (me?.role === "owner" || (!!myEmail && team?.createdBy === myEmail));

  const acceptInvite = async () => {
    if (!id || accepting) return;
    setAccepting(true);
    setAcceptErr(null);
    try {
      await acceptTeamInvite(id);
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === "already_member") {
        // Raced an earlier accept (another tab/MCP) — just resync.
        await refresh();
      } else {
        setAcceptErr(e instanceof Error ? e.message : "accept failed");
      }
    } finally {
      setAccepting(false);
    }
  };

  const addMember = async (e: FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!id || !email.includes("@")) {
      setMemberErr("enter the teammate's email");
      return;
    }
    setMemberBusy(true);
    setMemberErr(null);
    try {
      await addTeamMember(id, email, newRole);
      setNewEmail("");
      setNewRole("member");
      await refresh();
    } catch (e2) {
      setMemberErr(e2 instanceof Error ? e2.message : "add failed");
    } finally {
      setMemberBusy(false);
    }
  };

  const removeMember = async (email: string) => {
    if (!id) return;
    const leaving = email === myEmail;
    const ok = window.confirm(
      leaving
        ? `Leave "${team?.name ?? "this team"}"?`
        : `Remove ${email} from the team?`
    );
    if (!ok) return;
    setMemberErr(null);
    try {
      await removeTeamMember(id, email);
      if (leaving) {
        // No longer a member — drop the tenant selection if it pointed here.
        if (getTenantId() === id) setTenantId(null);
        navigate("/dashboard");
        return;
      }
      await refresh();
    } catch (e) {
      setMemberErr(e instanceof Error ? e.message : "remove failed");
    }
  };

  const setRow = (i: number, patch: Partial<SettingRow>) =>
    setRows((r) =>
      (r ?? []).map((row, j) => (j === i ? { ...row, ...patch } : row))
    );

  const removeRow = (i: number) =>
    setRows((r) => (r ?? []).filter((_, j) => j !== i));

  const addRow = () => {
    const key = newKey.trim();
    if (!key) return;
    if ((rows ?? []).some((r) => r.key === key)) {
      setSettingsErr(`"${key}" is already set`);
      return;
    }
    setRows((r) => [...(r ?? []), { key, value: newValue }]);
    setNewKey("");
    setNewValue("");
    setSettingsErr(null);
  };

  const saveSettings = async () => {
    if (!id || saving) return;
    setSaving(true);
    setSettingsErr(null);
    setSaveNote(null);
    try {
      const map: Record<string, string> = {};
      for (const r of rows ?? []) {
        if (r.key.trim()) map[r.key.trim()] = r.value;
      }
      await putTeamSettings(id, map);
      setSaveNote("saved");
      await refresh();
    } catch (e) {
      setSettingsErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(false);
    }
  };

  const suggestionsLeft = SUGGESTED_KEYS.filter(
    (k) => !(rows ?? []).some((r) => r.key === k)
  );

  return (
    <DashShell
      active={null}
      crumb={team?.name ?? "team"}
      userEmail={user?.email}
      entitlements={user?.ownedEntitlements}
      onSignOut={signOut}
    >
      {err && <p className="dsh-hint-error">Couldn't load team ({err})</p>}

      {team === null ? (
        !err && <p className="hint">Loading team…</p>
      ) : (
        <div className="dsh-stack">
          <Card className="dsh-headcard">
            <span className="dsh-tile">
              <Icon d={I.user} size={17} />
            </span>
            <div className="grow">
              <div className="title">{team.name}</div>
              <div className="id">{team.teamId}</div>
            </div>
            {team.createdAt && (
              <span className="meta">created {timeAgo(team.createdAt)}</span>
            )}
            <MonoChip tone="accent">
              {isInvited
                ? "you're invited"
                : isOwner
                  ? "you're an owner"
                  : "you're a member"}
            </MonoChip>
          </Card>

          {isInvited && (
            <Card style={{ borderColor: "var(--color-accent-line)" }}>
              <p style={{ margin: 0, fontSize: 13.5, color: "var(--color-fg-2)", lineHeight: "20px" }}>
                You've been invited to this team
                {me?.invitedBy ? (
                  <>
                    {" "}
                    by <span className="mono">{me.invitedBy}</span>
                  </>
                ) : null}
                . Accept to share its machines, agents, schedules, and
                overnight review.
              </p>
              <div className="dsh-form-actions" style={{ marginTop: 12 }}>
                <Btn variant="primary" disabled={accepting} onClick={acceptInvite}>
                  {accepting ? "Accepting…" : "Accept invite"}
                </Btn>
                {acceptErr && (
                  <span className="dsh-hint-error">{acceptErr}</span>
                )}
              </div>
            </Card>
          )}

          {/* members */}
          <div>
            <SecLabel
              action={
                <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-4)", whiteSpace: "nowrap" }}>
                  shares machines · agents · schedules
                </span>
              }
            >
              Members
            </SecLabel>
            {memberErr && <p className="dsh-hint-error">{memberErr}</p>}
            {team.members.length === 0 ? (
              <Card>
                <p className="hint" style={{ margin: 0 }}>
                  No members listed — the plane may still be rolling out
                  teams.
                </p>
              </Card>
            ) : (
              <Card pad={0}>
                {team.members.map((m) => (
                  <div className="dsh-member-row" key={m.email}>
                    <span className="email">{m.email}</span>
                    <MonoChip tone={m.role === "owner" ? "accent" : "neutral"}>
                      {m.role}
                    </MonoChip>
                    {m.status === "invited" && (
                      <StatusPill status="queued">invited</StatusPill>
                    )}
                    {m.email === myEmail && (
                      <span className="mono" style={{ fontSize: 11, color: "var(--color-fg-4)" }}>
                        you
                      </span>
                    )}
                    <span className="right">
                      {m.joinedAt && (
                        <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-5)" }}>
                          joined {timeAgo(m.joinedAt)}
                        </span>
                      )}
                      {(isOwner || m.email === myEmail) && (
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => removeMember(m.email)}
                        >
                          {m.email === myEmail ? "Leave" : "Remove"}
                        </Btn>
                      )}
                    </span>
                  </div>
                ))}
              </Card>
            )}
            {isOwner && (
              <Card style={{ marginTop: 10 }}>
                <form className="dsh-form" style={{ gap: 14 }} onSubmit={addMember}>
                  <div className="dsh-composer-grid">
                    <div>
                      <span className="dsh-field-label">Email</span>
                      <input
                        className="input mono"
                        placeholder="teammate@company.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="dsh-field-label">Role</span>
                      <select
                        className="input"
                        value={newRole}
                        onChange={(e) =>
                          setNewRole(e.target.value as "member" | "owner")
                        }
                      >
                        <option value="member">member</option>
                        <option value="owner">owner</option>
                      </select>
                    </div>
                  </div>
                  <div className="dsh-form-actions">
                    <Btn
                      variant="primary"
                      type="submit"
                      disabled={memberBusy || !newEmail.trim()}
                    >
                      {memberBusy ? "Adding…" : "Add member"}
                    </Btn>
                    <span className="dsh-form-note">
                      no invite email is sent — tell them; they'll see the
                      team on next sign-in
                    </span>
                  </div>
                </form>
              </Card>
            )}
          </div>

          {/* settings */}
          <div>
            <SecLabel
              action={
                <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-4)", whiteSpace: "nowrap" }}>
                  agents read these — the plane never interprets them
                </span>
              }
            >
              Settings
            </SecLabel>
            {(rows ?? []).length === 0 ? (
              <Card>
                <p className="hint" style={{ margin: 0 }}>
                  No settings yet. Teams usually start with{" "}
                  <code className="mono">githubOrg</code>,{" "}
                  <code className="mono">linearWorkspace</code>, and{" "}
                  <code className="mono">conventions</code>.
                </p>
              </Card>
            ) : (
              <Card pad={0}>
                {(rows ?? []).map((r, i) => (
                  <div className="dsh-setting-row" key={r.key}>
                    <span className="key">{r.key}</span>
                    {isOwner ? (
                      <input
                        className="input"
                        value={r.value}
                        onChange={(e) => setRow(i, { value: e.target.value })}
                      />
                    ) : (
                      <span className="val">{r.value || "—"}</span>
                    )}
                    {isOwner && (
                      <Btn
                        variant="danger"
                        size="sm"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </Btn>
                    )}
                  </div>
                ))}
              </Card>
            )}
            {isOwner && (
              <Card style={{ marginTop: 10 }}>
                <div className="dsh-form" style={{ gap: 14 }}>
                  <div className="dsh-composer-grid">
                    <div>
                      <span className="dsh-field-label">Key</span>
                      <input
                        className="input mono"
                        placeholder="githubOrg"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                      />
                      {suggestionsLeft.length > 0 && (
                        <div className="dsh-pills" style={{ marginTop: 8 }}>
                          {suggestionsLeft.map((k) => (
                            <button
                              key={k}
                              type="button"
                              className="dsh-pill"
                              style={{ height: 26, fontSize: 11 }}
                              onClick={() => setNewKey(k)}
                            >
                              {k}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="dsh-field-label">Value</span>
                      <textarea
                        className="input"
                        rows={1}
                        placeholder="value (conventions can be a paragraph)"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="dsh-form-actions">
                    <Btn
                      variant="outline"
                      size="sm"
                      disabled={!newKey.trim()}
                      onClick={addRow}
                    >
                      Add setting
                    </Btn>
                    <Btn
                      variant="primary"
                      size="sm"
                      disabled={saving}
                      onClick={saveSettings}
                    >
                      {saving ? "Saving…" : "Save settings"}
                    </Btn>
                    {settingsErr && (
                      <span className="dsh-hint-error">{settingsErr}</span>
                    )}
                    {saveNote && (
                      <span className="dsh-form-note">{saveNote}</span>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* enroll */}
          <div>
            <SecLabel
              action={
                <span className="mono" style={{ fontSize: 10.5, color: "var(--color-fg-4)", whiteSpace: "nowrap" }}>
                  --team machines belong to this team
                </span>
              }
            >
              Enroll a machine
            </SecLabel>
            <Card>
              <p className="hint" style={{ margin: 0 }}>
                On the machine joining the team's swarm, run:
              </p>
              <code className="dsh-enroll-code">
                nightswarmd enroll --base-url https://nightswarm.app --jwt
                &lt;your token&gt; --team {team.teamId}
              </code>
              <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
                Every member sees and can use machines enrolled this way.
                Re-enroll an existing machine with the flag to move it here;
                without <code className="mono">--team</code> it lands in your
                personal tenant.
              </p>
            </Card>
          </div>
        </div>
      )}
    </DashShell>
  );
}
